/**
 * Web deep-link workspace redirect.
 *
 * Email entity links use the canonical, OS-verified host `app.panluma.ai` so
 * they open the installed mobile app via Universal/App Links. For a tenant on a
 * `*.panluma.ai` subdomain the link also carries that host as a `?domain=` hint
 * (see backend `email_delivery._append_deep_link_domain`).
 *
 * When such a link is opened in a desktop browser it lands on `app.panluma.ai`,
 * but the subdomain tenant's session and branding live on their own origin —
 * so we redirect there (preserving path + remaining query + hash).
 *
 * SECURITY — no open redirect: we ONLY redirect within the `*.panluma.ai` zone
 * we control (wildcard DNS → our one CloudFront), which an attacker can never
 * own. The `?domain=` value is therefore NOT trusted to point anywhere else: a
 * non-`panluma.ai` host (incl. fully custom domains like `crm.acme.com`) is
 * never used as a redirect target — the hint is stripped and the app renders on
 * `app.panluma.ai`, where the existing post-login `LOGIN_ON_CUSTOM_DOMAIN`
 * bounce takes a custom-domain user to their origin. (A secure same-origin,
 * DB-backed validation endpoint would be needed to redirect to a fully custom
 * domain straight from the link — tracked as a follow-up.) No-op in Capacitor
 * (the native deep-link handler owns those URLs).
 *
 * See docs/platform/mobile-app.md § Deep links.
 */

import { isCapacitor } from '../api/config';
import { isPanlumaZoneHost } from './panlumaZone';

const DOMAIN_PARAM = 'domain';

/**
 * If the current URL carries a `?domain=` hint pointing at a different host in
 * the `*.panluma.ai` zone, redirect the browser there and resolve `true` (the
 * caller should skip rendering). Otherwise strip the hint and resolve `false`.
 */
export async function maybeRedirectToWorkspaceDomain(): Promise<boolean> {
  // Native apps deliver these URLs to the Capacitor deep-link handler, which
  // navigates in-app and never cross-origin.
  if (isCapacitor) {
    return false;
  }

  const params = new URLSearchParams(window.location.search);
  const hint = params.get(DOMAIN_PARAM);
  if (!hint) {
    return false;
  }

  const stripHint = () => {
    params.delete(DOMAIN_PARAM);
    const qs = params.toString();
    const clean = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`;
    window.history.replaceState(null, '', clean);
  };

  const target = hint.trim().toLowerCase();

  // Redirect ONLY to a different host inside the panluma.ai zone (open-redirect
  // guard). Same host, malformed hint, or any non-panluma.ai host → strip + stay.
  const redirectable =
    target !== window.location.hostname.toLowerCase() && isPanlumaZoneHost(target);

  if (!redirectable) {
    stripHint();
    return false;
  }

  params.delete(DOMAIN_PARAM);
  const qs = params.toString();
  const dest = `https://${target}${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`;
  window.location.replace(dest);
  return true;
}
